import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import { defineMessage, useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Cadenced from "@mapmycustomers/shared/types/base/Cadenced";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import RoundButton from "@app/component/RoundButton";
import { getFrequencyCustomInterval } from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useFrequencyIntervalOptions from "@app/util/frequency/useFrequencyIntervalOptions";
import { isSimpleCondition } from "@app/util/viewModel/assert";

const CLEAR_KEY = -1;
const CUSTOM_KEY = -2;
const NO_FREQUENCY_KEY = -3;

const customLabelMessage = defineMessage({
  id: "component.quickFilters.frequency.custom",
  defaultMessage: "Custom",
  description: "Custom option in dropdown in Frequency Status quick filter",
});

const noFrequencyLabelMessage = defineMessage({
  id: "component.quickFilters.frequency1.noFrequency",
  defaultMessage: "Unknown",
  description: "Empty in dropdown in Frequency Status quick filter",
});

interface Props {
  fieldName?: string;
  filter: FilterModel;
  onSetCustomFilters: (customFilters: Partial<FilterModel>) => void;
  onUpdateFilterModel: (filterModel: Partial<FilterModel>) => void;
  savedFrequencyInterval?: Cadenced["cadenceInterval"];
}

const FrequencyFilter: React.FC<Props> = ({
  fieldName = "cadenceInterval",
  filter,
  onSetCustomFilters,
  onUpdateFilterModel,
  savedFrequencyInterval,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const [status, setStatus] = useState<number>(0);

  const options = useFrequencyIntervalOptions(savedFrequencyInterval);

  const handleMenuClick = useCallback(
    ({ key }) => {
      const value = parseInt(key, 10);
      if (value === CUSTOM_KEY) {
        onSetCustomFilters({
          [fieldName]: { operator: FilterOperator.EQUALS, value: undefined },
        });
        analytics.clicked(["frequency Interval", "Custom"]);
        return;
      }
      if (value === NO_FREQUENCY_KEY) {
        onUpdateFilterModel({
          [fieldName]: { operator: FilterOperator.EMPTY, value: undefined },
        });
        analytics.clicked(["frequency Interval", "Empty"]);
        return;
      }
      onUpdateFilterModel({
        [fieldName]:
          value === CLEAR_KEY
            ? undefined
            : { operator: FilterOperator.EQUALS, value: options[value] },
      });
      analytics.clicked(["Frequency Interval"], { value: options[value] });
    },
    [analytics, fieldName, onSetCustomFilters, options, onUpdateFilterModel]
  );

  const formatOption = useCallback(
    (daysCount: number) =>
      intl.formatMessage(
        {
          id: "component.quickFilters.frequency.days",
          defaultMessage: "{count} {count, plural, one {day} other {days}}",
          description: "Days counter in dropdown in frequency Quick filter",
        },
        { count: daysCount }
      ),
    [intl]
  );

  useEffect(() => {
    if (filter && fieldName in filter) {
      const filterCondition = filter[fieldName];
      if (isSimpleCondition(filterCondition)) {
        setStatus(
          options.includes(filterCondition.value)
            ? filterCondition.value
            : filterCondition.operator === FilterOperator.EMPTY
            ? NO_FREQUENCY_KEY
            : 0
        );
      }
    } else {
      setStatus(0);
    }
  }, [fieldName, filter, options]);

  return (
    <Dropdown
      menu={{
        items: [
          ...(status !== 0
            ? [
                {
                  key: CLEAR_KEY,
                  label: (
                    <b>
                      {intl.formatMessage({
                        id: "component.quickFilters.frequency.clear",
                        defaultMessage: "Clear filter",
                        description: "Clear option in dropdown in frequency Status Quick filter",
                      })}
                    </b>
                  ),
                },
              ]
            : []),
          ...options.map((value, index) => ({
            key: index,
            label: formatOption(value),
          })),
          { key: NO_FREQUENCY_KEY, label: intl.formatMessage(noFrequencyLabelMessage) },
          { key: CUSTOM_KEY, label: intl.formatMessage(customLabelMessage) },
        ],
        onClick: handleMenuClick,
      }}
      trigger={["click"]}
    >
      <RoundButton active={status !== 0} hasMenu>
        {intl.formatMessage(
          {
            id: "component.quickFilters.frequency.title",
            defaultMessage:
              "Frequency Interval{isSet, select, true {: {valueName}} other {}}{isEmpty, select, true {: Unknown} other {}}",
            description: "Frequency quick filter button's title",
          },
          {
            isEmpty: status === NO_FREQUENCY_KEY,
            isSet: status > 0,
            valueName: formatOption(status ?? 0),
          }
        )}
      </RoundButton>
    </Dropdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  savedFrequencyInterval: getFrequencyCustomInterval(state),
});

export default connect(mapStateToProps)(FrequencyFilter);
